import { API, Card, CheckBox, Explination, Help, Input, Select, Submit, ViewHeader, useGet, useGetAll } from '@enkla-gui'
import { Select as AntSelect, Divider } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { icons } from '@config'
import { notify } from '@/_helpers'
import { useForm } from 'react-hook-form'

function TypeButton({ type, isSelected, onClick }) {
  return (
    <button
      type='button'
      className={`filter-toggle ${isSelected ? 'active' : ''}`}
      aria-pressed={isSelected}
      onClick={onClick}
    >
      <i className={`fas ${type.icon || 'fa-home'} me-1`} />
      {type.name}
    </button>
  )
}

export default function CreateEntity() {
  const { register, handleSubmit, reset, getValues, setValue, watch } = useForm()
  const history = useHistory()
  const { parentType: urlParentType, parentId: urlParentId } = useParams()

  const { data: companies } = useGet({ endpoint: 'api/company' })
  const { data: allTypes } = useGet({ endpoint: 'api/entity/types' })
  const { data: parentableEntities, loading: entitiesLoading } = useGetAll({ endpoint: 'api/entity', filter: { rentable: false } })

  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedParent, setSelectedParent] = useState(null)
  const [parentType, setParentType] = useState('company') // 'company' or 'entity'
  const [activeType, setActiveType] = useState(null)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)
  const [initializedFor, setInitializedFor] = useState(null)

  useEffect(() => {
    const currentParamKey = urlParentType && urlParentId ? `${urlParentType}:${urlParentId}` : null

    if (initializedFor === currentParamKey) return

    if (urlParentType === 'company' && urlParentId && companies?.data) {
      const company = companies.data.find(c => c.id === urlParentId)
      if (company) {
        setSelectedCompany(urlParentId)
        setSelectedParent('__direct__')
        setParentType('company')
        setValue('company_id', urlParentId)
        setInitializedFor(currentParamKey)
      }
    } else if (urlParentType === 'entity' && urlParentId && parentableEntities?.length) {
      const entity = parentableEntities.find(e => e.id === urlParentId)
      if (entity) {
        setSelectedCompany(entity.company_id)
        setSelectedParent(urlParentId)
        setParentType('entity')
        setValue('company_id', entity.company_id)
        setValue('parent_id', urlParentId)
        setInitializedFor(currentParamKey)
      }
    }
  }, [urlParentType, urlParentId, companies, parentableEntities, initializedFor, setValue])

  const availableTypes = useMemo(() => {
    if (!allTypes) return []
    if (parentType === 'company') {
      return allTypes
    }
    const parent = parentableEntities?.find(e => e.id === selectedParent)
    if (!parent?.type?.level) return allTypes
    return allTypes.filter(type => type.level > parent.type.level)
  }, [allTypes, parentType, selectedParent, parentableEntities])

  const parentEntities = useMemo(() => {
    if (!parentableEntities || !selectedCompany) return []
    return parentableEntities
      .filter(e => e.company_id === selectedCompany)
      .sort((a, b) => (a.type?.level || 0) - (b.type?.level || 0))
  }, [parentableEntities, selectedCompany])

  useEffect(() => {
    if (parentType === 'company' && selectedCompany) {
      const company = companies?.data?.find(c => c.id === selectedCompany)
      if (company) {
        reset({
          address: company.address,
          postal_code: company.postal_code,
          city: company.city,
          country: company.country || 'Sverige',
          company_id: selectedCompany,
          parent_id: null
        })
      }
    } else if (parentType === 'entity' && selectedParent) {
      const parent = parentableEntities?.find(e => e.id === selectedParent)
      if (parent) {
        reset({
          address: parent.address,
          postal_code: parent.postal_code,
          city: parent.city,
          country: parent.country || 'Sverige',
          company_id: parent.company_id,
          parent_id: selectedParent
        })
      }
    }
  }, [selectedCompany, selectedParent, parentType, companies, parentableEntities])

  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.find(t => t.id === activeType)) {
      setActiveType(null)
    }
  }, [availableTypes, activeType])

  const onSubmit = data => {
    if (!activeType) {
      notify.error('Välj en objektstyp')
      return
    }

    setSending(true)
    data.entity_type_id = activeType

    API.post('api/entity', data)
      .then(res => {
        notify.toast(`${res.entity.name} skapad`)
        if (parentType === 'entity' && selectedParent) {
          history.push(`/entity/${selectedParent}`)
        } else if (selectedCompany) {
          history.push(`/company/${selectedCompany}`)
        } else {
          history.push('/entity/overview')
        }
        setSending(false)
      })
      .catch(([err, res]) => {
        setError({
          message: res.message,
          data: res.data
        })
        setSending(false)
      })
  }

  const printTypeFields = () => {
    if (!activeType || !availableTypes.length) return null

    const aet = availableTypes.find(e => e.id === activeType)
    if (!aet) return null

    const fields = aet?.fields || {}
    const output = []
    const bools = []
    let orderedFields = []

    if (aet?.rentable) {
      output.push(
        <React.Fragment key="rent-fields">
          <div className='col-12'><Divider orientation="left" plain><small style={{ color: '#8c8c8c' }}>Hyresinformation</small></Divider></div>
          <div className='col-6'>
            <Input
              tip='Hyran som ska betalas per betalningsintervall'
              controls={{ register }}
              name='fee'
              options={{ required: false }}
              label={`Hyra (exmoms) per ${{ 'MONTHLY': 'månad', 'QUARTERLY': 'kvartal', 'YEARLY': 'år', 'NONE': 'betalningsintervall' }[watch('payment_interval') || 'NONE']}`}
              type='number'
              error={error}
            />
          </div>
          <Select
            controls={{ register }}
            name='payment_interval'
            options={{ required: false }}
            label='Betalningsintervall'
            cols='col-6 mb-3'
            values={[
              { key: 'Månadsvis', value: 'MONTHLY' },
              { key: 'Kvartalsvis', value: 'QUARTERLY' },
              { key: 'Årligen', value: 'YEARLY' }
            ]}
          />
          <div className='col-6'>
            <div className='form-group mb-3'>
              <label>Pris per m² <Explination tip='Vad betyder pris per m2?' /></label>
              <br />
              <CheckBox controls={{ register, getValues, setValue }} name='fee_as_m2' options={{ required: false }} label='Pris per m²' />
              {(!!watch('fee_as_m2') || !!getValues('fee_as_m2')) && (
                <div style={{ fontSize: '12px', color: '#7E8299', marginTop: '4px' }}>
                  {watch('fields.area') && parseFloat(watch('fields.area')) > 0 ? (
                    <span>
                      Hyra per {{ 'MONTHLY': 'månad', 'QUARTERLY': 'kvartal', 'YEARLY': 'år', 'NONE': 'betalningsintervall' }[watch('payment_interval') || 'NONE']}: {
                        (parseFloat(watch('fee') || 0) * parseFloat(watch('fields.area') || 0)).toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })
                      }
                    </span>
                  ) : (
                    <span>Förhandsvisning av hyra visas efter att area har fyllts i</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </React.Fragment>
      )
    }

    for (let [key, value] of Object.entries(fields)) {
      orderedFields.push({ key, value })
    }

    orderedFields = orderedFields.sort((a, b) => a.value.order - b.value.order)
    for (let { key, value } of orderedFields) {
      switch (value.type) {
        case 'string':
        case 'number':
          output.push(
            <Input
              key={key}
              controls={{ register }}
              error={error}
              name={`fields.${key}`}
              options={{ required: false }}
              label={value.title}
              type={value.type}
              cols='col-6 mb-3'
            />
          )
          break
        case 'boolean':
          bools.push(
            <div key={key} className='col-6'>
              <div className='form-group mb-3'>
                <label>{value.title}</label>
                <br />
                <CheckBox controls={{ register, getValues, setValue }} name={`fields.${key}`} options={{ required: false }} label={value.title} />
              </div>
            </div>
          )
          break
        case 'list':
          output.push(
            <Select
              key={key}
              controls={{ register }}
              name={`fields.${key}`}
              options={{ required: false }}
              label={value.title}
              cols='col-6 mb-3'
              values={value.options.map((option) => ({
                key: option,
                value: option
              }))}
            />
          )
          break
        default:
          break
      }
    }
    return output.concat(bools)

  }

  const selectedTypeName = availableTypes.find(t => t.id === activeType)?.name || 'Objekt'
  const canSubmit = selectedCompany && activeType && selectedParent

  return (
    <>
      <ViewHeader
        icon={icons.entity}
        title='Skapa objekt'
        subtitle='Hem'
        return={{ link: '#!/entity/overview', title: 'Objekt' }}
      />

      <Card

        title={`Skapa ${selectedTypeName}`}
        loading={!companies || !allTypes}
        icon={icons.entity}
        buttons={{
          footer: [
            { callback: () => history.goBack(), text: 'Avbryt', bg: 'light' },
            <Submit
              key="submit"
              onSubmit={handleSubmit(onSubmit)}
              text={`Skapa ${selectedTypeName}`}
              sending={sending}
              disabled={!canSubmit}
            />
          ]
        }}
      >
        <div className='row mb-3'>
          <div className='col-12'>
            <div className='form-group'>
              <label>Företag</label>
              <AntSelect
                style={{ width: '100%' }}
                placeholder='Välj Företag'
                value={selectedCompany}
                onChange={(value) => {
                  setSelectedCompany(value)
                  setSelectedParent(null)
                  setValue('company_id', value)
                }}
                options={companies?.data?.map(c => ({
                  value: c.id,
                  label: c.name
                })) || []}
                size='large'
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
          </div>
        </div>

        {selectedCompany && (
          <div className='row mb-3'>
            <div className='col-12'>
              <div className='form-group'>
                <label>Skapa under</label>
                <AntSelect
                  style={{ width: '100%' }}
                  placeholder='Välj var objektet ska skapas'
                  value={selectedParent}
                  onChange={(value) => {
                    if (value && value !== '__direct__') {
                      setSelectedParent(value)
                      setParentType('entity')
                      setValue('parent_id', value)
                    } else {
                      setSelectedParent(value === '__direct__' ? '__direct__' : null)
                      setParentType('company')
                      setValue('parent_id', null)
                    }
                  }}
                  options={[
                    {
                      value: '__direct__',
                      label: (
                        <span>
                          <i className='fas fa-briefcase' style={{ marginRight: '8px', color: '#1677ff' }} />
                          Direkt under {companies?.data?.find(c => c.id === selectedCompany)?.name || 'företaget'}
                        </span>
                      ),
                    },
                    ...parentEntities.map(e => ({
                      value: e.id,
                      label: (
                        <span>
                          <i className={`fas fa-fw ${e.type?.icon || 'fa-building'}`} style={{ marginRight: '8px', color: '#595959' }} />
                          {e.name}
                          <span style={{ color: '#8c8c8c', marginLeft: '8px', fontSize: '12px' }}>({e.type?.name})</span>
                        </span>
                      ),
                    }))
                  ]}
                  size='large'
                  showSearch
                  filterOption={(input, option) => {
                    if (option?.value === '__direct__') return true
                    return (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }}
                />
                {selectedParent === '__direct__' && (
                  <small class="text-muted" style={{marginTop: '4px', display: 'block' }}>
                    <em>Objektet kommer skapas direkt under företaget.</em>
                  </small>
                )}
                {selectedParent && selectedParent !== '__direct__' && (
                  <small class="text-muted" style={{marginTop: '4px', display: 'block' }}>
                    <em>Objektet kommer skapas under {parentEntities.find(e => e.id === selectedParent)?.name}.</em>
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedCompany && selectedParent && (
          <>
            <div className='row mb-3'>
              <div className='col-12'>
                <div className='form-group'>
                  <div className='filter-toggle-group'>
                    {(() => {
                      const sorted = [...availableTypes].sort((a, b) => a.level - b.level)
                      const rentable = sorted.filter(t => t.rentable)
                      const grundlaggande = sorted.filter(t => !t.rentable)
                      return (
                        <>
                          {grundlaggande.length > 0 && (
                            <>
                              <label style={{ width: '100%' }}>Grundläggande</label>
                              {grundlaggande.map(type => (
                                <TypeButton key={type.id} type={type} isSelected={activeType === type.id} onClick={() => setActiveType(type.id)} />
                              ))}
                            </>
                          )}
                          {rentable.length > 0 && (
                            <>
                              <label style={{ width: '100%' }}>Uthyrningsbara</label>
                              {rentable.map(type => (
                                <TypeButton key={type.id} type={type} isSelected={activeType === type.id} onClick={() => setActiveType(type.id)} />
                              ))}
                            </>
                          )}
                        </>
                      )
                    })()}
                  </div>
                  {error?.data?.entity_type_id && (
                    <div className='form-inline-error'>
                      <i className='fa fa-fw fa-exclamation-triangle' /> {error.data.entity_type_id[0]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {activeType && (
          <>
            <Divider orientation="left" plain><small style={{ color: '#8c8c8c' }}>Objektsinformation</small></Divider>

            <div className='row mb-3'>
              <Input controls={{ register }} name='name' label={`Namn`} error={error} placeholder={`Ex: ${selectedTypeName} 101`} />
              <Input controls={{ register }} name='reference' label='Objektsreferens' error={error} />
            </div>

            <div className='row'>
              {printTypeFields()}
            </div>

            <Divider orientation="left" plain><small style={{ color: '#8c8c8c' }}>Adress</small></Divider>

            <div className='row mb-3'>
              <Input controls={{ register }} name='address' options={{ required: false }} label='Adress' error={error} />
              <Input controls={{ register }} name='postal_code' options={{ required: false }} label='Postnummer' error={error} />
            </div>
            <div className='row mb-3'>
              <Input controls={{ register }} name='city' options={{ required: false }} label='Stad' error={error} />
              <Select
                controls={{ register }}
                name='country'
                options={{ required: false }}
                label='Land'
                error={error}
                preset='countries'
              />
            </div>
          </>
        )}

        {!selectedCompany && (
          <Help type='info'>
            <span>Börja med att välja ett företag för att skapa ett nytt objekt.</span>
          </Help>
        )}

        {selectedCompany && !selectedParent && (
          <Help type='info'>
            <span>Välj var objektet ska skapas – direkt under företaget eller under ett befintligt objekt.</span>
          </Help>
        )}

        {selectedCompany && selectedParent && !activeType && (
          <Help type='info'>
            <span>Välj en enhetstyp för att fortsätta.</span>
          </Help>
        )}
      </Card>
    </>
  )
}
